// File: GridDifAchieved.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiltroMeses } from "./chartsLogic/filterMonth";
import { obtenerTotales } from "./chartsLogic/datacharts";
import { Box, Button, Grid, Typography, CircularProgress } from "@mui/material";
import LineChartMUI from "./chartsLogic/LineMUICharts";
import CombinedCard from "./chartsLogic/CombinedCard";
import { formatYm } from "./chartsLogic/utils";

// Componente padre: Fetch, filtro y tarjetas globales
function GridDifAchieved({ dataOutputs = [], loading = false }) {
	const { t } = useTranslation();
	const [mesesFiltrados, setMesesFiltrados] = useState([]);
	const [mesesSeleccionados, setMesesSeleccionados] = useState([]);
	const optionsLoading = loading;
	useEffect(() => {
		if (!dataOutputs.length) {
			setMesesFiltrados([]);
			setMesesSeleccionados([]);
			return;
		}

		const entry = dataOutputs[0];
		const yearMonths = Object.keys(entry).filter((k) => /^\d{4}-\d{2}$/.test(k));
		const mesesI18n = yearMonths.map((ym) => ({
			value: ym,
			label: formatYm(ym, t, { short: true }),
		}));

		setMesesFiltrados(mesesI18n);
		setMesesSeleccionados(mesesI18n.map((m) => m.value));
	}, [dataOutputs, t]);

	const monthEntries = useMemo(() => {
		if (!dataOutputs.length) return [];
		const entry = dataOutputs[0];

		// 1. Sacamos ["2025-11","2025-12",…]
		const yearMonths = Object.keys(entry).filter((k) => /^\d{4}-\d{2}$/.test(k));

		// 2. Para cada "YYYY-MM" creamos un objeto con sólo los contratos de ese mes
		return yearMonths.map((ym) => {
			const [, mm] = ym.split("-");
			const mesNum = Number(mm);

			// protegemos el array de planning por si viniera undefined
			const planningArr = Array.isArray(entry.output_planning_monthly)
				? entry.output_planning_monthly
				: [];

			// ids de planning que corresponden a este mes
			const idsForMonth = planningArr.filter((o) => o.mes === mesNum).map((o) => o.id);

			// protegemos contrato_monthly y cada sección
			const contrato = entry.contrato_monthly || {};
			const diurnoArr = Array.isArray(contrato.Diurno) ? contrato.Diurno : [];
			const nocturnoArr = Array.isArray(contrato.Nocturno) ? contrato.Nocturno : [];
			const madrugadaArr = Array.isArray(contrato.Madrugada) ? contrato.Madrugada : [];

			return {
				contrato_monthly: {
					Diurno: diurnoArr.filter((item) => idsForMonth.includes(item.output_planning_monthly)),
					Nocturno: nocturnoArr.filter((item) => idsForMonth.includes(item.output_planning_monthly)),
					Madrugada: madrugadaArr.filter((item) => idsForMonth.includes(item.output_planning_monthly)),
				},
				output_planning_monthly: planningArr.filter((o) => idsForMonth.includes(o.id)),
			};
		});
	}, [dataOutputs]);

	const selMesNums = useMemo(
		() =>
			mesesSeleccionados.map((m) =>
				typeof m === "string" && m.includes("-") ? Number(m.split("-")[1]) : Number(m),
			),
		[mesesSeleccionados],
	);

	// Nuevos cálculos: cobertura y holgura reales y sugeridas
	const { covReal, holgReal, covSug, holgSug, hasSug } = useMemo(() => {
		const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

		// Para cada output extraigo sólo los meses seleccionados
		const valores = dataOutputs.map((out) => {
			const cifras = Object.entries(out)
				.filter(([k]) => /^\d{4}-\d{2}$/.test(k) && mesesSeleccionados.includes(k))
				.map(([, v]) => ({
					cov: v.porcentaje_cobertura,
					hol: v.porcentaje_holgura,
				}))
				.filter(({ cov, hol }) => typeof cov === "number" && typeof hol === "number");
			return cifras;
		});
		if (valores.length === 0) {
			return {
				covReal: 0,
				holgReal: 0,
				covSug: 0,
				holgSug: 0,
				hasSug: false,
			};
		}
		// Si sólo hay un output, lo trato como “real”
		if (valores.length === 1) {
			const arr = valores[0];
			return {
				covReal: avg(arr.map((x) => x.cov)),
				holgReal: avg(arr.map((x) => x.hol)),
				covSug: null,
				holgSug: null,
				hasSug: false,
			};
		}

		// Si vienen dos o más, el primero es sugerido y el segundo real
		return {
			covSug: avg(valores[0].map((x) => x.cov)),
			holgSug: avg(valores[0].map((x) => x.hol)),
			covReal: avg(valores[1].map((x) => x.cov)),
			holgReal: avg(valores[1].map((x) => x.hol)),
			hasSug: true,
		};
	}, [dataOutputs, mesesSeleccionados]);
	// Toggle select/deselect all
	const toggleAll = () => {
		if (mesesSeleccionados.length === mesesFiltrados.length) setMesesSeleccionados([]);
		else setMesesSeleccionados(mesesFiltrados.map((m) => m.value));
	};
	const normalizedOutputs = useMemo(() => {
		return dataOutputs.map((out) => {
			const norm = {};
			Object.entries(out).forEach(([key, val]) => {
				if (!/^\d{4}-\d{2}$/.test(key)) return;

				// Ahora sí tomo la parte del mes:
				const [, monthStr] = key.split("-");
				const month = Number(monthStr); // 11, 12, 1, 2…
				const monthKey = String(month); // "11","12", etc.

				norm[monthKey] = {
					personas_activas: val.personas_activas,
					demanda: val.demanda,
				};
			});
			return norm;
		});
	}, [dataOutputs]);

	const totales = useMemo(() => {
		if (!monthEntries.length || !selMesNums.length) {
			return { totalNE: 0, totalSP: 0 };
		}
		// Filtramos sólo los entries de los meses seleccionados
		const entriesToSum = monthEntries.filter((entry) =>
			entry.output_planning_monthly.some((o) => selMesNums.includes(o.mes)),
		);
		// Devolvemos los totales calculados por el helper
		return obtenerTotales(entriesToSum, selMesNums);
	}, [monthEntries, selMesNums]);
	return (
		<Box sx={{ width: "100%", display: "flex", justifyContent: "center", pt: 2 }}>
			<Box sx={{ width: "80%" }}>
				<Grid
					container
					spacing={2}
					alignItems="center"
					justifyContent={hasSug ? "flex-start" : "center"}
				>
					<Grid item xs={12}>
						<Typography variant="h5" align="center">
							{t("new_charts.title")}
						</Typography>
					</Grid>

					<Grid item xs={8}>
						<FiltroMeses
							mesesSeleccionados={mesesSeleccionados}
							setMesesSeleccionados={setMesesSeleccionados}
							mesesFiltrados={mesesFiltrados}
							optionsLoading={optionsLoading}
						/>
					</Grid>

					<Grid item xs={4}>
						<Button fullWidth variant="outlined" onClick={toggleAll}>
							{mesesSeleccionados.length === mesesFiltrados.length
								? t("new_charts.unselect_all")
								: t("new_charts.select_all")}
						</Button>
					</Grid>

					<Grid item xs={hasSug ? 6 : 12}>
						{optionsLoading ? (
							<Box sx={{ textAlign: "center", py: 2 }}>
								<CircularProgress size={24} />
								<Typography variant="body2" sx={{ mt: 1 }}>
									{t("new_charts.loading")}
								</Typography>
							</Box>
						) : dataOutputs.length === 0 || mesesSeleccionados.length === 0 ? (
							<Typography align="center" color="text.secondary" sx={{ py: 2 }}>
								{t("new_charts.no_data_found")}
							</Typography>
						) : (
							<CombinedCard
								title={t("new_charts.real_staffing")}
								dotacion={totales.totalNE}
								cobertura={covReal}
								holgura={holgReal}
								t={t}
							/>
						)}
					</Grid>

					{hasSug && (
						<Grid item xs={6}>
							<CombinedCard
								title={t("new_charts.target_staffing")}
								dotacion={totales.totalSP}
								cobertura={covSug}
								holgura={holgSug}
								t={t}
							/>
						</Grid>
					)}

					<Grid item xs={12}>
						{optionsLoading ? (
							<Box sx={{ textAlign: "center", py: 4 }}>
								<CircularProgress />
								<Typography variant="body2" sx={{ mt: 1 }}>
									{t("new_charts.loading")}
								</Typography>
							</Box>
						) : dataOutputs.length === 0 || mesesSeleccionados.length === 0 ? (
							<Typography align="center" color="text.secondary" sx={{ py: 4 }}>
								{t("new_charts.no_data_found")}
							</Typography>
						) : (
							<LineChartMUI data={normalizedOutputs} selectedMonths={selMesNums} />
						)}
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}

export { GridDifAchieved };
